import { useQueryClient } from '@tanstack/react-query';
import { useGlobalQuery, useGlobalMutation } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import { useErrorHandler } from '@/hooks/use-error-handler';
import type { UserRole } from '@/state/store/auth/index.type';
import {
  getAdminUsers,
  updateAdminUserRole,
  removeAdminUser,
  inviteAdminUser,
} from '../services/admin-users.service';
import type { InviteAdminUserInput } from '../types/admin-users.types';

export const useGetAdminUsers = (page = 0, pageSize = 50, search = '') => {
  return useGlobalQuery({
    queryKey: ['adminUsers', page, pageSize, search],
    queryFn: () => getAdminUsers(page, pageSize, search),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useUpdateAdminUserRole = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (variables: { userId: string; role: UserRole }) =>
      updateAdminUserRole(variables.userId, variables.role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast({ variant: 'success', title: 'User role updated' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to update user role' });
    },
  });
};

export const useRemoveAdminUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (userId: string) => removeAdminUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast({ variant: 'success', title: 'User removed' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to remove user' });
    },
  });
};

export const useInviteAdminUser = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { handleError } = useErrorHandler();

  return useGlobalMutation({
    mutationFn: (data: InviteAdminUserInput) => inviteAdminUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      toast({ variant: 'success', title: 'Invitation sent successfully' });
    },
    onError: (error: Error) => {
      handleError(error, { defaultMessage: 'Failed to send invitation' });
    },
  });
};
